import { z } from "zod";
import { LOOK_ALIKE_REASONS } from "../sourcing/supplier-names";

/**
 * Data imports (WP-3.3, ADR-0026): purchase history uploaded by `imports.manage` holders, applied
 * by the worker, and the supplier look-alike queue.
 */

export const IMPORT_SOURCES = ["purchase_history", "vendor_panel"] as const;
export type ImportSource = (typeof IMPORT_SOURCES)[number];

export const PURCHASE_HISTORY_IMPORT_MAX_BYTES = 100 * 1024 * 1024;
export const PURCHASE_HISTORY_IMPORT_MAX_ROWS = 500_000;
/** The vendor panel is a few hundred rows (ADR-0027). */
export const VENDOR_PANEL_IMPORT_MAX_BYTES = 10 * 1024 * 1024;
export const VENDOR_PANEL_IMPORT_MAX_ROWS = 20_000;

/** Why an upload is refused at once (400 `details.reason`): nothing is queued. */
export const IMPORT_FILE_PROBLEMS = ["empty", "too_large", "not_text", "missing_columns", "no_rows"] as const;
export type ImportFileProblem = (typeof IMPORT_FILE_PROBLEMS)[number];

export const IMPORT_RUN_STATUSES = ["QUEUED", "RUNNING", "APPLIED", "FAILED"] as const;
export type ImportRunStatus = (typeof IMPORT_RUN_STATUSES)[number];

/** Why a run failed as a whole (nothing was changed). */
export const IMPORT_RUN_FAILURES = [
  "too_many_rows",
  "not_text",
  "missing_columns",
  "no_rows",
  "file_missing",
  "processing_failed",
] as const;
export type ImportRunFailure = (typeof IMPORT_RUN_FAILURES)[number];

/**
 * Problems with one line. Errors reject the line, and with it its whole requisition
 * (`requisition_rejected` marks the requisition's other lines); warnings keep the line.
 */
export const IMPORT_LINE_ERRORS = [
  "missing_value",
  "invalid_number",
  "invalid_date",
  "value_too_long",
  "requisition_rejected",
  "invalid_value",
] as const;
export const IMPORT_LINE_WARNINGS = [
  "negative_amount",
  "date_out_of_range",
  "ignored_invalid_date",
  "ignored_invalid_number",
  "several_requisition_dates",
  "activity_filled",
  "duplicate_dropped",
  "conflicting_rates",
  "rate_not_available",
  "unknown_role_area",
] as const;
export const IMPORT_LINE_PROBLEMS = [...IMPORT_LINE_ERRORS, ...IMPORT_LINE_WARNINGS] as const;
export type ImportLineError = (typeof IMPORT_LINE_ERRORS)[number];
export type ImportLineWarning = (typeof IMPORT_LINE_WARNINGS)[number];
export type ImportLineProblem = (typeof IMPORT_LINE_PROBLEMS)[number];

export const purchaseHistoryCountsSchema = z.object({
  /** Data lines read (header excluded, blank lines skipped). */
  linesRead: z.number().int(),
  linesStored: z.number().int(),
  requisitions: z.object({
    added: z.number().int(),
    replaced: z.number().int(),
    unchanged: z.number().int(),
    rejected: z.number().int(),
  }),
  suppliers: z.object({
    /** New suppliers (a match key not seen before). */
    created: z.number().int(),
    /** New spellings folded into an existing supplier automatically (same match key). */
    newSpellings: z.number().int(),
    /** Look-alike pairs added to the supplier queue. */
    queued: z.number().int(),
  }),
  errors: z.partialRecord(z.enum(IMPORT_LINE_ERRORS), z.number().int()),
  warnings: z.partialRecord(z.enum(IMPORT_LINE_WARNINGS), z.number().int()),
});
export type PurchaseHistoryCounts = z.infer<typeof purchaseHistoryCountsSchema>;

/** A vendor panel run (ADR-0027 §1): the panel as it stands after the run replaced it. */
export const vendorPanelCountsSchema = z.object({
  linesRead: z.number().int(),
  linesStored: z.number().int(),
  panel: z.object({
    suppliers: z.number().int(),
    entries: z.number().int(),
    individuals: z.number().int(),
    valueContracts: z.number().int(),
  }),
  suppliers: z.object({ created: z.number().int(), newSpellings: z.number().int(), queued: z.number().int() }),
  errors: z.partialRecord(z.enum(IMPORT_LINE_ERRORS), z.number().int()),
  warnings: z.partialRecord(z.enum(IMPORT_LINE_WARNINGS), z.number().int()),
});
export type VendorPanelCounts = z.infer<typeof vendorPanelCountsSchema>;

export const importRunCountsSchema = z.union([purchaseHistoryCountsSchema, vendorPanelCountsSchema]);
export type ImportRunCounts = z.infer<typeof importRunCountsSchema>;

export function isPanelCounts(counts: ImportRunCounts): counts is VendorPanelCounts {
  return "panel" in counts;
}

export const importRunSchema = z.object({
  id: z.uuid(),
  source: z.enum(IMPORT_SOURCES),
  fileName: z.string(),
  sizeBytes: z.number().int(),
  /** Display name of the importer; null if that user no longer exists. */
  importedBy: z.string().nullable(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  status: z.enum(IMPORT_RUN_STATUSES),
  failureReason: z.enum(IMPORT_RUN_FAILURES).nullable(),
  encoding: z.enum(["utf-8", "windows-1252"]).nullable(),
  unknownColumns: z.array(z.string()),
  /** Columns read but deliberately not stored (personal data with no use, ADR-0026 §1). */
  ignoredColumns: z.array(z.string()),
  /** Null until the run has finished reading the file. */
  counts: importRunCountsSchema.nullable(),
});
export type ImportRun = z.infer<typeof importRunSchema>;

export const importProblemSchema = z.object({
  /** 1-based line of the file (the header is line 1). No values: they are commercial data. */
  line: z.number().int(),
  code: z.enum(IMPORT_LINE_PROBLEMS),
  severity: z.enum(["ERROR", "WARNING"]),
});
export type ImportProblem = z.infer<typeof importProblemSchema>;

/** Problems kept per run; the counts always cover every line. */
export const IMPORT_PROBLEMS_KEPT = 1_000;

export const importRunDetailSchema = importRunSchema.extend({
  problems: z.array(importProblemSchema),
  /** More problems than IMPORT_PROBLEMS_KEPT: the list shows the first ones. */
  problemsTruncated: z.boolean(),
});
export type ImportRunDetail = z.infer<typeof importRunDetailSchema>;

export const importRunListQuerySchema = z.object({
  source: z.enum(IMPORT_SOURCES).optional(),
});
export type ImportRunListQuery = z.infer<typeof importRunListQuerySchema>;

// ───────────── Supplier look-alike queue ─────────────

export const SUPPLIER_MATCH_STATUSES = ["OPEN", "SAME", "DIFFERENT"] as const;
export type SupplierMatchStatus = (typeof SUPPLIER_MATCH_STATUSES)[number];

export const supplierSummarySchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  /** Every spelling seen in imports. */
  spellings: z.array(z.string()),
  /** Purchase-history lines with an order from this supplier. No amounts (ADR-0026 §7). */
  orderCount: z.number().int(),
  lastOrderDate: z.string().nullable(),
});
export type SupplierSummary = z.infer<typeof supplierSummarySchema>;

export const supplierMatchSchema = z.object({
  id: z.uuid(),
  reason: z.enum(LOOK_ALIKE_REASONS),
  status: z.enum(SUPPLIER_MATCH_STATUSES),
  createdAt: z.string(),
  /** The older supplier: a "same" decision keeps this one. */
  kept: supplierSummarySchema,
  /** The newer supplier: merged into `kept` on "same". */
  other: supplierSummarySchema,
  resolvedBy: z.string().nullable(),
  resolvedAt: z.string().nullable(),
});
export type SupplierMatch = z.infer<typeof supplierMatchSchema>;

export const supplierMatchListQuerySchema = z.object({
  status: z.enum(SUPPLIER_MATCH_STATUSES).default("OPEN"),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export type SupplierMatchListQuery = z.infer<typeof supplierMatchListQuerySchema>;

export const supplierMatchListResponseSchema = z.object({
  items: z.array(supplierMatchSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});
export type SupplierMatchListResponse = z.infer<typeof supplierMatchListResponseSchema>;

export const supplierMatchResolveSchema = z.object({ decision: z.enum(["same", "different"]) });
export type SupplierMatchResolve = z.infer<typeof supplierMatchResolveSchema>;
